import { useMutation, useQueryClient } from '@tanstack/react-query';
import { channelsApi } from '../services';
import type { ChannelResponse, HostedPageConfig, WidgetFeatures } from '@troia-v3/types';

/**
 * Hook to update widget configuration
 *
 * @example
 * const updateConfig = useUpdateWidgetConfig();
 * updateConfig.mutate({ channelId: '123', config: { brandColor: '#ff0000' } });
 */
export const useUpdateWidgetConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channelId, config }: {
      channelId: string;
      config: {
        brandColor?: string;
        position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
        welcomeMessage?: string;
        agentName?: string;
        suggestions?: Array<{ icon: string; text: string }>;
        hostedPage?: HostedPageConfig;
        features?: WidgetFeatures;
      };
    }) => channelsApi.updateWidgetConfig(channelId, config),
    onSuccess: (data: ChannelResponse) => {
      // Invalidate channels list
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      // Update cached channel data
      queryClient.setQueryData(['channel', data.id], data);
      // Invalidate widget config cache so modal loads fresh data
      queryClient.invalidateQueries({ queryKey: ['widget-config', data.id] });
      // Invalidate embed script cache (config affects embed output)
      queryClient.invalidateQueries({ queryKey: ['widget-embed-script', data.id] });
    },
  });
};
